import { DependencyList, useEffect, useRef } from 'react';

/**
 * A wrapper around useEffect for async operations.
 *
 * Solves two problems:
 * 1. useEffect doesn't accept async functions directly
 * 2. Provides a function `getIsMounted` check to avoid setting state after component unmounts
 * 3. Provide a function `getIsStale` to check if the effect is stale (i.e., dependencies have changed)
 * @param effect An async function that receives `getIsMounted` and `getIsStale` functions.
 * @param deps Dependency list for the effect.
 */
export const useAsyncEffect = (
  effect: (
    getIsMounted: () => boolean,
    getIsStale: () => boolean,
  ) => Promise<void | (() => void)>,
  deps?: DependencyList,
): void => {
  const isMounted = useRef(false);

  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  }, []);

  useEffect(() => {
    const staleStatus = { isStale: false };

    const runEffect = async () => {
      try {
        return await effect(
          () => isMounted.current,
          () => staleStatus.isStale,
        );
      } catch (error) {
        console.error('Error in useAsyncEffect:', error);
        return undefined;
      }
    };

    const result = runEffect();

    return () => {
      staleStatus.isStale = true;

      result.then((callback) => {
        if (callback) {
          callback();
        }
      });
    };
  }, deps);
};
